import React from 'react'
import styled from 'styled-components'
import * as linkify from 'linkifyjs'
import reactStringReplace from 'react-string-replace'
import { Link as BuiLink, colors } from '@blocket/blocket-ui'
import { phoneNumberRegEx, emailRegEx, linksRegEx } from 'utils/regex'
import checkExternalContacts from '../checkExternalContacs'
import formatHref from '../formatHref'
import WarningModalContent from 'components/sharedComponents/Modals/WarningModal'
import { trackModalView } from 'tracking/chat'

export const checkBlocketUrl = (url) => {
  let isBlocketUrl = false
  if (url.toLowerCase().indexOf('blocket.se') >= 0) {
    isBlocketUrl = true
  }
  return isBlocketUrl
}

const Link = styled(BuiLink)`
  cursor: pointer;
`

const SenderLink = styled(Link)`
  color: ${colors.white100};
  text-decoration: underline;
  &:hover {
    color: ${colors.white100};
    opacity: 0.8;
  }
`

// if you run this with an array of messages, make sure to descend the order of strings to make a more exact match
const appendJsxLink = (
  message,
  formatProperty,
  modalState,
  setModalState,
  isRecipentMessage,
  selectedChannelUrl,
  modalDisabled = false,
) => {
  const { type, regexMatch, isBlocketUrl } = formatProperty
  const isExternalLink = type === 'link'

  return reactStringReplace(message, regexMatch, (match, i) => {
    if (isRecipentMessage && !isBlocketUrl && !modalDisabled) {
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          as="span"
          key={i + match}
          onClick={(e) => {
            e.preventDefault()
            setModalState({
              open: !modalState.open,
              children: (
                <WarningModalContent
                  type={type}
                  match={match}
                  selectedChannelUrl={selectedChannelUrl}
                  closeModal={() => setModalState({ ...modalState, open: false })}
                  viewTracking={() =>
                    trackModalView({ objectId: 'safety_tips', objectCategory: type })
                  }
                />
              ),
            })
          }}
        >
          {match}
        </Link>
      )
    }

    const linkProps = {
      key: i + match,
      href: formatHref(type, match),
      target: isExternalLink ? '_blank' : '_self',
      rel: isExternalLink ? 'noopener noreferrer' : undefined,
    }

    if (!isRecipentMessage) {
      return <SenderLink {...linkProps}>{match}</SenderLink>
    }

    return <Link {...linkProps}>{match}</Link>
  })
}

export const setFormatTextMessageProperties = (message) => {
  const formatProperties = []
  // escape if message isn't valid
  if (typeof message === 'undefined' || typeof message !== 'string') {
    return formatProperties
  }

  // handles phone numbers
  if (phoneNumberRegEx().test(message)) {
    message.match(phoneNumberRegEx()).forEach((number) => {
      number = number.trim()
      const properties = {}
      if (typeof number !== 'undefined') {
        properties.type = 'phone'
        properties.regexMatch = number
        formatProperties.push(properties)
      }
    })
  }

  if (emailRegEx().test(message)) {
    message.match(emailRegEx()).forEach((regexMatch) => {
      if (!linkify.test(regexMatch, 'email')) return

      const properties = {}
      if (typeof regexMatch !== 'undefined') {
        properties.type = 'mail'
        properties.regexMatch = regexMatch
        formatProperties.push(properties)
      }
    })
  }

  if (linksRegEx().test(message)) {
    message.match(linksRegEx()).forEach((regexMatch) => {
      if (!linkify.test(regexMatch)) return

      const properties = {}
      if (typeof regexMatch !== 'undefined') {
        properties.type = 'link'
        properties.regexMatch = regexMatch
        properties.isBlocketUrl = checkBlocketUrl(regexMatch)
        formatProperties.push(properties)
      }
    })
  }
  return formatProperties.sort((a, b) => b.regexMatch.length - a.regexMatch.length)
}

const formatMessageLinks = (
  unformattedMessage,
  modalState,
  setModalState,
  isRecipentMessage,
  selectedChannelUrl,
  modalDisabled,
) => {
  let { message } = unformattedMessage
  let containsExternalContactInfo = false
  const formatTextMessageProperties = setFormatTextMessageProperties(message)

  if (!formatTextMessageProperties) {
    return unformattedMessage
  }

  if (isRecipentMessage && checkExternalContacts(message)) {
    containsExternalContactInfo = true
  }

  if (formatTextMessageProperties.length) {
    formatTextMessageProperties.forEach((formatProperty) => {
      message = appendJsxLink(
        message,
        formatProperty,
        modalState,
        setModalState,
        isRecipentMessage,
        selectedChannelUrl,
        modalDisabled,
      )
    })
  }

  return {
    ...unformattedMessage,
    message,
    ...(containsExternalContactInfo && { containsExternalContactInfo }),
  }
}

export default formatMessageLinks
